import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { FormBadge, FormReason, FormZone } from "@jackedlogs/shared";

import { Eyebrow } from "../primitives/Eyebrow";
import type { UiTheme } from "../theme";
import { makeGlanceCardStyles } from "./glanceCardStyles";

// Home readiness flag. A qualitative Ready / Caution / Rest badge derived from
// logged training load (see homeFormBadge.ts) — never a number, never colour
// alone (the zone WORD always carries the meaning), framed as a gentle flag
// and NOT a medical/injury prediction. Tapping it opens Progress for the full
// picture (home-redesign.md). Lifted into @jackedlogs/ui with the Home hero
// surface; theme is host-provided.
type Props = {
  badge: FormBadge | null;
  theme: UiTheme;
  onPress: () => void;
};

const ZONE_LABEL: Record<FormZone, { key: string; def: string }> = {
  ready: { key: "mobile.home.form.ready", def: "Ready" },
  caution: { key: "mobile.home.form.caution", def: "Caution" },
  rest: { key: "mobile.home.form.rest", def: "Rest" },
};

const REASON_TEXT: Record<FormReason, { key: string; def: string }> = {
  noData: {
    key: "mobile.home.form.reasonNoData",
    def: "Log a session to start tracking your readiness.",
  },
  wellRecovered: {
    key: "mobile.home.form.reasonWellRecovered",
    def: "Well rested since your last session — good to push.",
  },
  balanced: {
    key: "mobile.home.form.reasonBalanced",
    def: "Your training load looks balanced.",
  },
  freshStart: {
    key: "mobile.home.form.reasonFreshStart",
    def: "Getting back into it — ease in and build a base.",
  },
  shortRest: {
    key: "mobile.home.form.reasonShortRest",
    def: "Back-to-back sessions — keep an eye on recovery.",
  },
  highFrequency: {
    key: "mobile.home.form.reasonHighFrequency",
    def: "A high-volume week — consider a lighter day.",
  },
  loadSpike: {
    key: "mobile.home.form.reasonLoadSpike",
    def: "Your load jumped sharply vs your recent average.",
  },
  longGap: {
    key: "mobile.home.form.reasonLongGap",
    def: "It's been a while — start light to ramp back up.",
  },
};

export function FormBadgeCard({ badge, theme, onPress }: Props) {
  const { t } = useTranslation();
  const glance = makeGlanceCardStyles(theme);
  const styles = makeStyles(theme);
  if (badge === null) return null;

  const p = theme.palette;
  const tone =
    badge.zone === "ready"
      ? { dot: p.complete, fill: p.completeFill }
      : badge.zone === "caution"
        ? { dot: p.warn, fill: p.warnFill }
        : { dot: p.danger, fill: p.dangerFill };
  const zoneWord = t(ZONE_LABEL[badge.zone].key, {
    defaultValue: ZONE_LABEL[badge.zone].def,
  });
  const reasonText = t(REASON_TEXT[badge.reason].key, {
    defaultValue: REASON_TEXT[badge.reason].def,
  });

  return (
    <Pressable
      accessibilityRole="button"
      // The label carries the reason too — the actionable insight must
      // reach a screen-reader user, not just the zone word.
      accessibilityLabel={`${t("mobile.home.form.title", {
        defaultValue: "Readiness",
      })}: ${zoneWord}. ${reasonText}`}
      onPress={onPress}
      style={({ pressed }) => [
        glance.card,
        { backgroundColor: tone.fill },
        pressed && glance.pressed,
      ]}
    >
      <View style={glance.textCol}>
        <Eyebrow theme={theme}>
          {t("mobile.home.form.title", { defaultValue: "Readiness" })}
        </Eyebrow>
        <View style={styles.zoneRow}>
          <View style={[styles.dot, { backgroundColor: tone.dot }]} />
          <Text style={[styles.zone, { color: tone.dot }]}>{zoneWord}</Text>
        </View>
        <Text style={styles.reason}>{reasonText}</Text>
      </View>
      <Text style={glance.chevron}>›</Text>
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    zoneRow: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    dot: { width: 12, height: 12, borderRadius: 6 },
    zone: {
      fontSize: t.sizes.lg,
      fontWeight: "700",
      letterSpacing: -0.3,
    },
    reason: {
      color: p.ink2,
      fontSize: t.sizes.base,
      lineHeight: t.sizes.base * 1.35,
    },
    chevron: { color: p.ink3, fontSize: t.sizes.xl },
  });
}
